import { Avatar, Col, Row, Text } from 'components';
import { theme } from 'lib';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CATEGORY_LABELS, fmtDate, resolveContentPreview } from '../constants';
import { AutoBlockedBadge, CategorySeverityBadge } from './ui';

const ActionButton = styled.button`
  height: 34px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: CeraPro;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s, background-color 0.15s;
  border: 1px solid
    ${(props) => (props.danger ? '#FECDD3' : props.primary ? theme.GREEN : theme.GREY_30)};
  background: ${(props) => (props.primary ? '#E7FBF3' : '#fff')};
  color: ${(props) => (props.danger ? '#E11D48' : props.primary ? '#0C9C74' : theme.GREY_70)};

  &:hover:not(:disabled) {
    background: ${(props) => (props.danger ? '#FFF1F2' : props.primary ? '#D4F7EA' : '#fff')};
    border-color: ${(props) => (props.danger ? '#E11D48' : props.primary ? '#0C9C74' : theme.SECONDARY)};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const Thumb = styled.a`
  display: block;
  width: 88px;
  height: 88px;
  border-radius: 10px;
  border: 1px solid ${theme.GREY_20};
  background-image: url('${(props) => props.src}');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
`;

const TextPreview = styled.div`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${theme.GREY_10};
  border: 1px solid ${theme.GREY_20};
  font-family: CeraPro;
  font-size: 13.5px;
  line-height: 1.5;
  color: ${theme.GREY_80};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 140px;
  overflow: auto;
`;

const FlaggedCard = ({ item, busy, onApprove, onReject, onViewContext }) => {
  const preview = resolveContentPreview(item);
  const user = item.user;
  // Texts hard-blocked by a severe word-list hit: nothing to approve/reject
  // (the content was never saved) — the card is informational, Dismiss clears it.
  const autoBlocked = item.action === 'Block' && !!item.profanityLevel;

  return (
    <Col
      noFlex
      wid="100%"
      pad="16px 18px"
      gap="14px"
      style={{
        background: '#fff',
        borderRadius: '12px',
        border: `1px solid ${theme.GREY_20}`,
        boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
      }}
    >
      {/* Header: author + reason + date */}
      <Row center between noFlex wid="100%" gap="12px" wrapFlex>
        <Row center noFlex gap="10px" style={{ minWidth: 0 }}>
          <Avatar user={user} avatar={user?.avatar} small />
          <Col noFlex style={{ minWidth: 0 }}>
            <Text b2 bold marg="0" style={{ fontSize: '13.5px' }}>
              {user?.name || user?.username || 'Unknown user'}
            </Text>
            <Text c1 col={theme.GREY_60} marg="1px 0 0" style={{ fontSize: '11.5px' }}>
              {user?.username ? `@${user.username}` : user?.email || '—'} · {item.contentType}
            </Text>
          </Col>
        </Row>

        <Row center noFlex gap="10px">
          {autoBlocked ? (
            <AutoBlockedBadge />
          ) : (
            <CategorySeverityBadge
              category={item.triggeredCategory}
              severity={item.triggeredSeverity}
            />
          )}
          <Text c2 col={theme.GREY_50} style={{ fontSize: '11px' }}>
            {fmtDate(item.createdAt)}
          </Text>
        </Row>
      </Row>

      {/* Matched word-list terms, each linking to the term in the Profanity tab */}
      {autoBlocked && item.matchedTerms?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {item.matchedTerms.map((t) => (
            <Link
              key={t}
              to={`?tab=profanity&search=${encodeURIComponent(t)}`}
              className="text-[11px] font-mono text-[#E11D48] bg-[#FFF1F2] border border-[#FECDD3] rounded-full px-2 py-0.5 hover:border-[#E11D48] transition-colors"
            >
              {t}
            </Link>
          ))}
        </div>
      )}

      {/* Flagged content */}
      {preview.deleted ? (
        <TextPreview style={{ fontStyle: 'italic', color: theme.GREY_50 }}>
          The underlying content has been deleted.
        </TextPreview>
      ) : preview.isImage ? (
        preview.imageUrl ? (
          <Thumb href={preview.imageUrl} target="_blank" rel="noreferrer" src={preview.imageUrl} />
        ) : (
          <TextPreview style={{ fontStyle: 'italic', color: theme.GREY_50 }}>
            Image unavailable.
          </TextPreview>
        )
      ) : (
        <TextPreview>{preview.text || 'No text preview available.'}</TextPreview>
      )}

      {/* Actions */}
      <Row center between noFlex wid="100%">
        <Text c1 col={theme.GREY_60} style={{ fontSize: '11.5px' }}>
          {autoBlocked
            ? 'Blocked automatically — the content was never published.'
            : `Flagged for ${CATEGORY_LABELS[item.triggeredCategory] || item.triggeredCategory || 'review'}`}
        </Text>
        <Row center noFlex gap="8px">
          <ActionButton disabled={busy} onClick={() => onViewContext(item)}>
            View context
          </ActionButton>
          {autoBlocked ? (
            <ActionButton disabled={busy} onClick={() => onApprove(item)}>
              {busy === 'approved' ? 'Dismissing…' : 'Dismiss'}
            </ActionButton>
          ) : (
            <>
              <ActionButton danger disabled={busy} onClick={() => onReject(item)}>
                {busy === 'rejected' ? 'Rejecting…' : 'Reject'}
              </ActionButton>
              <ActionButton primary disabled={busy} onClick={() => onApprove(item)}>
                {busy === 'approved' ? 'Approving…' : 'Approve'}
              </ActionButton>
            </>
          )}
        </Row>
      </Row>
    </Col>
  );
};

export default FlaggedCard;
